'use client';

import { useLocale, useTranslations } from 'next-intl';
import NextLink from 'next/link';
import {
  Box,
  Container,
  Typography,
  Button,
  Divider,
  Chip,
} from '@mui/material';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { LandingHeader, LandingFooter } from '../../../../features/landing';
import { BlogContent } from '../../blog/components/BlogContent';
import { ComparisonTable } from './ComparisonTable';
import type { ComparisonMeta } from '../lib/comparisons';

interface ComparisonTemplateProps {
  meta: ComparisonMeta;
  content: string;
}

/**
 * ComparisonTemplate - Full comparison landing page layout.
 * Renders headline, comparison table, markdown content, FAQ, and CTA sections.
 * Follows the blog layout pattern from story 6-10.
 */
export function ComparisonTemplate({
  meta,
  content,
}: ComparisonTemplateProps) {
  const locale = useLocale();
  const t = useTranslations('creator.compare');

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}
    >
      <LandingHeader />

      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 }, flex: 1 }}>
        {/* Hero Section */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontWeight: 700,
              mb: 2,
              fontSize: { xs: '1.75rem', md: '2.5rem' },
            }}
          >
            {meta.headline}
          </Typography>
          <Typography
            variant="h6"
            component="p"
            sx={{
              color: 'text.secondary',
              maxWidth: 600,
              mx: 'auto',
              mb: 3,
              fontSize: { xs: '1rem', md: '1.25rem' },
            }}
          >
            {meta.description}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
            {meta.tags.slice(0, 4).map((tag) => (
              <Chip
                key={tag}
                label={tag}
                size="small"
                variant="outlined"
                color="primary"
              />
            ))}
          </Box>
        </Box>

        {/* Quick Stats */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: { xs: 2, md: 4 },
            mb: 4,
            flexWrap: 'wrap',
          }}
        >
          <StatBox
            label={t('botySetup')}
            value={meta.setupTimeBoty}
            highlight
          />
          <StatBox
            label={`${meta.competitorName} ${t('setup')}`}
            value={meta.setupTimeCompetitor}
          />
          {meta.pricingCompetitor && (
            <StatBox
              label={`${meta.competitorName} ${t('pricing')}`}
              value={meta.pricingCompetitor}
            />
          )}
        </Box>

        {/* Top CTA */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            component={NextLink}
            href={`/${locale}`}
            variant="contained"
            size="large"
            startIcon={<RocketLaunchIcon />}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              px: 4,
              py: 1.5,
              fontSize: '1rem',
            }}
          >
            {t('tryCta')}
          </Button>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Comparison Table */}
        <ComparisonTable
          features={meta.features}
          competitorName={meta.competitorName}
          title={t('featureComparison')}
        />

        <Divider sx={{ my: 4 }} />

        {/* Main Content (markdown) */}
        <BlogContent>{content}</BlogContent>

        {/* Related Blog Post Link */}
        {meta.relatedBlogPost && (
          <Box
            sx={{
              mt: 4,
              p: 3,
              bgcolor: 'grey.50',
              borderRadius: 2,
              border: 1,
              borderColor: 'divider',
            }}
          >
            <Typography variant="body1" sx={{ mb: 1 }}>
              {t('readDetailedComparison')}
            </Typography>
            <Button
              component={NextLink}
              href={`/${locale}/blog/${meta.relatedBlogPost}`}
              variant="outlined"
              sx={{ textTransform: 'none' }}
            >
              {t('readBlogPost')}
            </Button>
          </Box>
        )}

        <Divider sx={{ my: 4 }} />

        {/* Bottom CTA */}
        <Box
          sx={{
            textAlign: 'center',
            py: 4,
            my: 4,
            bgcolor: 'primary.50',
            borderRadius: 2,
            border: 1,
            borderColor: 'primary.100',
          }}
        >
          <Typography
            variant="h5"
            component="p"
            sx={{ fontWeight: 600, mb: 2 }}
          >
            {t('bottomCtaTitle')}
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: 'text.secondary', mb: 3 }}
          >
            {t('bottomCtaSubtitle')}
          </Typography>
          <Button
            component={NextLink}
            href={`/${locale}`}
            variant="contained"
            size="large"
            startIcon={<RocketLaunchIcon />}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              px: 4,
              py: 1.5,
              fontSize: '1rem',
            }}
          >
            {t('tryCta')}
          </Button>
        </Box>
      </Container>

      <LandingFooter />
    </Box>
  );
}

/**
 * StatBox - Small stat display for quick comparison metrics.
 */
function StatBox({
  label,
  value,
  highlight = false,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <Box
      sx={{
        textAlign: 'center',
        p: 2,
        borderRadius: 2,
        bgcolor: highlight ? 'primary.50' : 'grey.50',
        border: 1,
        borderColor: highlight ? 'primary.200' : 'divider',
        minWidth: 120,
      }}
    >
      <Typography
        variant="h6"
        sx={{
          fontWeight: 700,
          color: highlight ? 'primary.main' : 'text.primary',
        }}
      >
        {value}
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {label}
      </Typography>
    </Box>
  );
}
